/**
 * 文件定位：
 * 1. 当前文件负责生成装配节点删除/切换前的 Mate 清理影响计划；
 * 2. 只消费项目模型、Mate 文档和 Object3D 只读身份，不直接操作 Viewer 或 Controller；
 * 3. 让删除与切换共用同一套“会清理哪些 Mate”的判断，避免提示和实际清理不一致。
 */

import type {Object3D} from 'three';
import {
  collectAssemblyDocumentNodeIdentityLookupIds,
  createAssemblyMateDisplayLabelById,
  readComponentInstanceAssemblyNodeId,
  removeAssemblyMateDocumentReferencesByAssemblySubtree,
  type AssemblyDocumentNode,
  type AssemblyMateDefinition,
  type AssemblyMateDocumentModel,
  type ModelAssetNode,
  type ProjectModelLoadResult,
} from '@puppycad/cad-core';
import type {FeatureTreeDeleteConfirmItem} from '@/views/editor/requestFeatureTreeDeleteConfirmation';

/**
 * 装配节点删除/切换时的 Mate 清理影响计划。
 */
export interface AssemblySubtreeMateRemovalImpactPlan {
  /** 删除前的完整 Mate 文档。 */
  readonly beforeDocument: AssemblyMateDocumentModel;
  /** 清理悬挂引用后的完整 Mate 文档。 */
  readonly afterDocument: AssemblyMateDocumentModel;
  /** 会随装配节点删除/切换一起移除的 Mate。 */
  readonly removedMates: readonly AssemblyMateDefinition[];
  /** 复用删除确认弹框展示的关联项目。 */
  readonly relatedItems: readonly FeatureTreeDeleteConfirmItem[];
}

/**
 * 装配节点删除/切换时的旧子树目标。
 *
 * 设计说明：
 * rebase 到支持 root `.puppy` 内嵌 direct child 后，切换目标不一定存在于
 * `ProjectModelLoadResult.root.children`。因此 Mate 清理不能只依赖 `ModelAssetNode`，
 * 必须把模型树节点、工作装配文档节点和运行时对象子树身份统一成一个目标描述。
 */
export interface AssemblySubtreeMateRemovalTarget {
  /** 删除或切换的旧子树结构 key。 */
  readonly nodeKey: string;
  /** 项目模型中的旧子树根节点；root `.puppy` 内嵌节点可能不存在。 */
  readonly modelNode?: ModelAssetNode | null;
  /** 工作装配文档中的旧子树节点；用于补齐 `.puppy` 内嵌节点的稳定 occurrence 身份。 */
  readonly documentNode?: AssemblyDocumentNode | null;
  /** 旧运行时对象根 ID；用于遍历 Object3D 子树读取局部 assemblyNodeId。 */
  readonly runtimeRootObjectIds?: readonly string[];
}

/**
 * 创建装配节点 Mate 清理影响计划的输入。
 */
export interface CreateAssemblySubtreeMateRemovalImpactPlanInput {
  /** 删除或切换的旧子树目标。 */
  readonly target: AssemblySubtreeMateRemovalTarget;
  /** 当前完整 Mate 文档。 */
  readonly document: AssemblyMateDocumentModel;
  /** 当前项目模型加载结果，用于从 sceneBindings 找到运行时对象身份。 */
  readonly projectModelLoadResult?: ProjectModelLoadResult | null;
  /** 按 Object3D UUID 读取当前场景对象。 */
  readonly resolveSceneObjectById?: (objectId: string) => Object3D | null;
  /** 当前时间戳，测试可注入固定值。 */
  readonly now?: number;
}

/**
 * 收集模型节点子树中的所有 nodeKey。
 *
 * @param node 子树根节点。
 * @returns 子树内从根到后代的 nodeKey 列表。
 */
function collectProjectModelSubtreeNodeKeys(node: ModelAssetNode): string[] {
  return [
    node.nodeKey,
    ...node.children.flatMap((child) => collectProjectModelSubtreeNodeKeys(child)),
  ];
}

/**
 * 向身份集合追加非空字符串。
 *
 * @param identityIds 身份集合。
 * @param value 候选身份。
 */
function addIdentityId(identityIds: Set<string>, value: string | null | undefined): void {
  const trimmedValue = value?.trim() ?? '';
  if (trimmedValue) {
    identityIds.add(trimmedValue);
  }
}

/**
 * 批量追加非空身份。
 *
 * @param identityIds 身份集合。
 * @param values 候选身份列表。
 */
function addIdentityIds(identityIds: Set<string>, values: readonly string[]): void {
  values.forEach((value) => {
    addIdentityId(identityIds, value);
  });
}

/**
 * 收集 Object3D 子树上携带的运行时装配节点身份。
 *
 * 设计说明：
 * 删除一级节点时，Mate 可能记录的是子装配包内部的局部 assemblyNodeId，而不是 OpenProject
 * 的一级 nodeKey。场景对象 metadata 是当前运行时最接近 Mate 选择/高亮/求解的一层身份来源，
 * 因此这里复用 cad-core 的统一读取函数，不在 web-client 重新理解 metadata 结构。
 *
 * @param root 运行时对象树根。
 * @param identityIds 输出身份集合。
 */
function collectRuntimeAssemblyNodeIdsFromObjectTree(
  root: Object3D,
  identityIds: Set<string>,
): void {
  root.traverse((object) => {
    addIdentityId(identityIds, readComponentInstanceAssemblyNodeId(object));
  });
}

/**
 * 解析删除子树对应的运行时对象根。
 *
 * @param input 清理计划输入。
 * @param subtreeNodeKeys 删除目标模型子树 nodeKey。
 * @returns 当前场景中命中的对象根。
 */
function resolveRuntimeObjectsForAssemblySubtree(
  input: CreateAssemblySubtreeMateRemovalImpactPlanInput,
  subtreeNodeKeys: ReadonlySet<string>,
): Object3D[] {
  if (!input.resolveSceneObjectById) {
    return [];
  }

  const runtimeRootObjectIds = [
    ...(input.target.runtimeRootObjectIds ?? []),
    ...(input.projectModelLoadResult?.sceneBindings ?? [])
      .filter((binding) => subtreeNodeKeys.has(binding.nodeKey))
      .map((binding) => binding.objectId),
  ];

  return runtimeRootObjectIds
    .map((objectId) => input.resolveSceneObjectById?.(objectId) ?? null)
    .filter((object): object is Object3D => Boolean(object))
    .filter((object, index, array) => array.indexOf(object) === index);
}

/**
 * 收集装配节点删除/切换目标对应的真实运行时对象根。
 *
 * @param input 删除影响分析输入。
 * @returns 由显式目标和项目 sceneBindings 共同解析出的去重对象根。
 */
export function collectAssemblySubtreeRemovalRuntimeRootObjects(
  input: CreateAssemblySubtreeMateRemovalImpactPlanInput,
): readonly Object3D[] {
  const subtreeNodeKeys = input.target.modelNode
    ? collectProjectModelSubtreeNodeKeys(input.target.modelNode)
    : [];
  return resolveRuntimeObjectsForAssemblySubtree(input, new Set(subtreeNodeKeys));
}

/**
 * 收集装配节点删除/切换时用于 Mate 清理的全部身份。
 *
 * @param input 清理计划输入。
 * @returns 模型子树 nodeKey 与运行时 Object3D metadata 身份的并集。
 */
export function collectAssemblySubtreeMateRemovalAssemblyNodeIds(
  input: CreateAssemblySubtreeMateRemovalImpactPlanInput,
): readonly string[] {
  const identityIds = new Set<string>();
  const subtreeNodeKeys = input.target.modelNode
    ? collectProjectModelSubtreeNodeKeys(input.target.modelNode)
    : [];

  addIdentityId(identityIds, input.target.nodeKey);
  subtreeNodeKeys.forEach((nodeKey) => {
    addIdentityId(identityIds, nodeKey);
  });
  if (input.target.documentNode) {
    addIdentityIds(identityIds, collectAssemblyDocumentNodeIdentityLookupIds(input.target.documentNode));
  }
  resolveRuntimeObjectsForAssemblySubtree(input, new Set(subtreeNodeKeys)).forEach((object) => {
    collectRuntimeAssemblyNodeIdsFromObjectTree(object, identityIds);
  });

  return Array.from(identityIds);
}

/**
 * 解析删除确认框中的 Mate 显示名。
 *
 * 设计说明：
 * 删除确认框展示给用户的是 SolidWorks 设计树语义名称，不能展示 `mateId` 这类内部稳定 ID。
 * 当文档索引异常缺失时，兜底只展示参与实体，避免把内部标识泄漏到 UI。
 *
 * @param mate Mate Definition。
 * @param labelById Mate ID 到设计树展示名称的映射。
 * @returns 可展示给用户的 Mate 名称。
 */
function resolveAssemblyMateDeletionDisplayName(
  mate: AssemblyMateDefinition,
  labelById: ReadonlyMap<string, string>,
): string {
  return labelById.get(mate.mateId)
    ?? `${mate.firstEntity.label}, ${mate.secondEntity.label}`;
}

/**
 * 创建装配节点删除/切换时的 Mate 清理影响计划。
 *
 * @param input 清理计划输入。
 * @returns 有 Mate 被清理时返回影响计划，否则返回 null。
 */
export function createAssemblySubtreeMateRemovalImpactPlan(
  input: CreateAssemblySubtreeMateRemovalImpactPlanInput,
): AssemblySubtreeMateRemovalImpactPlan | null {
  const result = removeAssemblyMateDocumentReferencesByAssemblySubtree({
    document: input.document,
    assemblyNodeIds: collectAssemblySubtreeMateRemovalAssemblyNodeIds(input),
    now: input.now,
  });
  if (result.removedMates.length === 0) {
    return null;
  }

  const mateDisplayLabelById = createAssemblyMateDisplayLabelById(input.document.mates);

  return {
    beforeDocument: input.document,
    afterDocument: result.document,
    removedMates: result.removedMates,
    relatedItems: result.removedMates.map((mate) => ({
      id: mate.mateId,
      label: resolveAssemblyMateDeletionDisplayName(mate, mateDisplayLabelById),
      kind: 'mate',
    })),
  };
}
